import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "../../../../i18n/LanguageContext";
import EmailAnalysisAPI from "../../services/EmailAnalysisAPI";
import ScamReportAPI from "../../services/ScamReportAPI";
import AccountAPI from "../../services/AccountAPI";
import { useUser } from "../../context/UserContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faEnvelope,
  faBan,
} from "@fortawesome/free-solid-svg-icons";
import "../../css/Common.css";
import "./css/EmailManualScan.css";
import "./css/EmailAnalysisSimple.css";

// Import desktop and mobile specific components
import EmailAnalysisSimpleDesktop from "./EmailAnalysisSimpleDesktop";
import EmailAnalysisSimpleMobile from "./EmailAnalysisSimpleMobile";

const EmailAnalysisSimple = ({
  onToggleView,
  targetAnalysisKey,
  onTargetSelected,
  setHeroContent,
}) => {
  const { loggedInUser } = useUser();
  const { t } = useTranslation();
  const userUuid = loggedInUser?.uuid || loggedInUser?.id;

  // Mobile detection constant
  const isMobile = () => window.innerWidth <= 768;

  // Core state
  const [analyses, setAnalyses] = useState([]);
  const [error, setError] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [reportIdMap, setReportIdMap] = useState({});
  // True when the member's organization has turned off inbound email-forwarding
  // analysis (tenant privacy control). Forwarded emails will not be analyzed.
  const [analysisDisabled, setAnalysisDisabled] = useState(false);
  const loadAllAnalysesRef = useRef(null);
  const checkQuotaStatusRef = useRef(null);
  const loadAnalysisForNotificationRef = useRef(null);
  // "idle" | "waiting" | "timedOut" — set after the user says they forwarded an email
  const [awaitState, setAwaitState] = useState("idle");
  const awaitBaselineRef = useRef(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Mobile tab state - for tab navigation on mobile
  const [activeTab, setActiveTab] = useState("results"); // results, details

  // Mobile initial view - determine if we should show tabs or desktop layout
  const [showMobileTabView, setShowMobileTabView] = useState(isMobile());

  // Load all analyses on mount
  useEffect(() => {
    loadAllAnalysesRef.current?.();
    checkQuotaStatusRef.current?.();
  }, [userUuid]);

  // Check whether this member's organization disabled inbound email analysis
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await AccountAPI.getAccountByUserUuid();
        const accountInfo = response?.data || response;
        const disabled =
          (accountInfo?.disableInboundEmailAnalysis ??
            accountInfo?.disable_inbound_email_analysis) === true;
        if (!cancelled) {
          setAnalysisDisabled(disabled);
        }
      } catch (err) {
        // Non-fatal: if we cannot determine the setting, do not show the banner.
        console.error(
          "EmailAnalysisSimple: Failed to load account analysis setting:",
          err,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userUuid]);

  // Poll for the forwarded email so the user doesn't have to keep pressing Refresh
  useEffect(() => {
    if (awaitState !== "waiting") return undefined;
    loadAllAnalysesRef.current?.();
    // Skip polls while the tab is hidden; the overall timeout still applies.
    const poll = setInterval(() => {
      if (!document.hidden) loadAllAnalysesRef.current?.();
    }, 10000);
    const timeout = setTimeout(() => setAwaitState("timedOut"), 180000);
    return () => {
      clearInterval(poll);
      clearTimeout(timeout);
    };
  }, [awaitState]);

  // Open the newly arrived analysis automatically
  useEffect(() => {
    if (awaitState !== "waiting" || !awaitBaselineRef.current) return;
    const arrived = analyses.find((a) => !awaitBaselineRef.current.has(a.key));
    if (!arrived) return;
    setAwaitState("idle");
    setSelectedAnalysis(arrived);
    if (window.innerWidth <= 768) setActiveTab("details");
  }, [analyses, awaitState]);

  const startAwaitingForward = () => {
    awaitBaselineRef.current = new Set(analyses.map((a) => a.key));
    setAwaitState("waiting");
  };

  const cancelAwaitingForward = () => {
    awaitBaselineRef.current = null;
    setAwaitState("idle");
  };

  // Check quota status from localStorage
  const checkQuotaStatus = () => {
    const quotaKey = `email_quota_exceeded_${userUuid}_${new Date().toDateString()}`;
    const exceeded = localStorage.getItem(quotaKey) === "true";
    setQuotaExceeded(exceeded);
  };
  checkQuotaStatusRef.current = checkQuotaStatus;

  // Handle window resize to update mobile view state
  useEffect(() => {
    const handleResize = () => {
      const mobileView = isMobile();
      setShowMobileTabView(mobileView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-select analysis if targetAnalysisKey is provided (for notification navigation)
  useEffect(() => {
    if (targetAnalysisKey && analyses.length > 0) {
      console.log(
        "EmailAnalysisSimple: Looking for target analysis with key:",
        targetAnalysisKey,
      );
      console.log("EmailAnalysisSimple: Available analyses:", analyses.length);
      console.log(
        "EmailAnalysisSimple: First few analysis keys:",
        analyses.slice(0, 5).map((a) => ({
          key: a.key,
          keyType: typeof a.key,
          keyLength: a.key ? a.key.length : "undefined",
        })),
      );
      console.log("EmailAnalysisSimple: Target key details:", {
        key: targetAnalysisKey,
        keyType: typeof targetAnalysisKey,
        keyLength: targetAnalysisKey ? targetAnalysisKey.length : "undefined",
      });

      // Extract filename from full S3 path for comparison
      const targetFilename = targetAnalysisKey.split("/").pop();
      console.log(
        "EmailAnalysisSimple: Extracted target filename:",
        targetFilename,
      );

      const targetAnalysis = analyses.find((analysis) => {
        // Compare against the filename portion only
        console.log("analysis.key = ", analysis.key);
        console.log("targetFilename = ", targetFilename);
        const match = analysis.key && analysis.key.endsWith(targetFilename);
        if (
          !match &&
          analysis.key &&
          analysis.key.includes("i6e8kva55un3tpj4q0jlm7988uj076f0pohr33o1")
        ) {
          console.log("EmailAnalysisSimple: Found similar key but no match:", {
            analysisKey: analysis.key,
            targetFilename: targetFilename,
            equal: analysis.key === targetFilename,
            analysisKeyLength: analysis.key.length,
            targetFilenameLength: targetFilename.length,
          });
        }
        return match;
      });
      if (targetAnalysis) {
        console.log(
          "EmailAnalysisSimple: Found target analysis, auto-selecting:",
          targetAnalysis,
        );
        setSelectedAnalysis(targetAnalysis);
        // Clear force refresh flag after successful selection
        if (onTargetSelected) {
          onTargetSelected();
        }

        // Enhanced navigation to details - always show details view for notification navigation
        const showDetailsFromNotification =
          localStorage.getItem("notificationShowDetails") === "true";
        console.log(
          "EmailAnalysisSimple: Show details from notification flag:",
          showDetailsFromNotification,
        );
        console.log(
          "EmailAnalysisSimple: Checking mobile status for details navigation:",
          isMobile(),
        );

        if (isMobile()) {
          // For mobile, switch to details tab to show the analysis
          console.log(
            "EmailAnalysisSimple: Setting details tab for notification navigation",
          );
          setActiveTab("details");
        } else {
          // For desktop/tablet, ensure the details pane is visible and scroll to it
          console.log(
            "EmailAnalysisSimple: Desktop mode - scrolling to details pane",
          );
          // Multiple scroll attempts to ensure success
          const scrollAttempts = [100, 300, 500, 800];
          scrollAttempts.forEach((delay, index) => {
            setTimeout(() => {
              scrollToAnalysisDetails();
              console.log(
                `EmailAnalysisSimple: Notification navigation scroll attempt ${
                  index + 1
                } - Scrolled to details`,
              );
            }, delay);
          });
        }

        // Clear the target after selection already called above
      } else {
        console.log(
          "EmailAnalysisSimple: Target analysis not found in current results, trying broader search",
        );
        // Fallback to broader search
        loadAnalysisForNotificationRef.current?.(targetAnalysisKey);
      }
    }
  }, [targetAnalysisKey, analyses, onTargetSelected]);

  // Load specific analysis for notification if not found in recent results
  const loadAnalysisForNotification = async (analysisKey) => {
    try {
      console.log(
        "EmailAnalysisSimple: Loading specific analysis for notification:",
        analysisKey,
      );
      // Try searching without date restrictions by bypassing default filters
      const results = await EmailAnalysisAPI.searchAnalyses({
        userUuid,
        bypassDefaultFilters: true,
      });
      console.log(
        "EmailAnalysisSimple: Broader search results:",
        results.length,
      );
      console.log(
        "EmailAnalysisSimple: First few results keys:",
        results.slice(0, 5).map((a) => ({
          key: a.key,
          keyType: typeof a.key,
          keyLength: a.key ? a.key.length : "undefined",
        })),
      );

      // Extract filename from full S3 path for comparison
      const targetFilename = analysisKey.split("/").pop();
      console.log(
        "EmailAnalysisSimple: Broader search - Extracted target filename:",
        targetFilename,
      );

      const targetAnalysis = results.find((analysis) => {
        // Compare against the filename portion only
        const match = analysis.key && analysis.key.endsWith(targetFilename);
        if (
          !match &&
          analysis.key &&
          analysis.key.includes("i6e8kva55un3tpj4q0jlm7988uj076f0pohr33o1")
        ) {
          console.log(
            "EmailAnalysisSimple: Broader search - Found similar key but no match:",
            {
              analysisKey: analysis.key,
              targetFilename: targetFilename,
              equal: analysis.key === targetFilename,
              analysisKeyLength: analysis.key.length,
              targetFilenameLength: targetFilename.length,
            },
          );
        }
        return match;
      });

      if (targetAnalysis) {
        console.log(
          "EmailAnalysisSimple: Found target analysis in broader search:",
          targetAnalysis,
        );
        // Add the found analysis to current analyses if not already present
        setAnalyses((prevAnalyses) => {
          const targetFilename = analysisKey.split("/").pop();
          const existing = prevAnalyses.find(
            (a) => a.key && a.key.endsWith(targetFilename),
          );
          if (!existing) {
            return [targetAnalysis, ...prevAnalyses];
          }
          return prevAnalyses;
        });
        setSelectedAnalysis(targetAnalysis);

        // Show details view
        if (isMobile()) {
          setActiveTab("details");
        } else {
          // For desktop/tablet, scroll to details section
          const scrollAttempts = [200, 500, 800];
          scrollAttempts.forEach((delay, index) => {
            setTimeout(() => {
              scrollToAnalysisDetails();
              console.log(
                `EmailAnalysisSimple: Broader search scroll attempt ${
                  index + 1
                } - Scrolled to details`,
              );
            }, delay);
          });
        }
      } else {
        console.warn(
          "EmailAnalysisSimple: Target analysis not found even in broader search",
        );
        // Persist key so NotificationSimpleMobile can pick it up after mounting
        sessionStorage.setItem("kinshield_failed_analysis_key", analysisKey);
        // Also notify already-mounted components (e.g. NotificationDropdown on desktop)
        window.dispatchEvent(
          new CustomEvent("email-analysis-not-found", {
            detail: { analysisKey },
          }),
        );
        if (setHeroContent) {
          // Navigate back to Notifications so the user can dismiss the stale notification
          setHeroContent("Notifications");
        } else {
          setError(t("email.error.notFound"));
        }
      }

      if (onTargetSelected) {
        onTargetSelected();
      }
    } catch (error) {
      console.error(
        "EmailAnalysisSimple: Error loading analysis for notification:",
        error,
      );
      // Persist key so NotificationSimpleMobile can pick it up after mounting
      sessionStorage.setItem("kinshield_failed_analysis_key", analysisKey);
      // Also notify already-mounted components
      window.dispatchEvent(
        new CustomEvent("email-analysis-not-found", {
          detail: { analysisKey },
        }),
      );
      if (setHeroContent) {
        setHeroContent("Notifications");
      } else {
        setError(t("email.error.loadNotifFailed"));
      }
      if (onTargetSelected) {
        onTargetSelected();
      }
    }
  };
  loadAnalysisForNotificationRef.current = loadAnalysisForNotification;

  // Auto-save scam reports for forwarded email analyses that don't have one yet.
  // Backend dedup (exact match on user+type+input) ensures no duplicates on reload.
  const ensureReportsForScamItems = async (transformedResults) => {
    const scamItems = transformedResults.filter(
      (item) =>
        item.analysis?.analysisResult?.isScam === true && !item.reportId,
    );
    if (scamItems.length === 0) return;

    const newMap = {};
    for (const item of scamItems) {
      try {
        const result = item.analysis.analysisResult;
        const saveResponse = await ScamReportAPI.saveReport({
          analysisType: "EMAIL_FORWARDED_ANALYSIS",
          analysisTitle: result.subject || "Email Scam Detected",
          analysisDetail:
            "Forwarded email analysis detected suspicious content",
          analysisInput: item.originalEmailContent || "",
          isScam: true,
          confidence: result.confidence || 0,
          riskLevel:
            result.confidence >= 0.7
              ? "HIGH"
              : result.confidence >= 0.4
                ? "MEDIUM"
                : "LOW",
          explanation: result.reasoning || "",
          suggestions: result.suggestions || "",
        });
        if (saveResponse.reportId) {
          newMap[item.key] = saveResponse.reportId;
        }
      } catch (err) {
        console.error(
          "[EmailAnalysisSimple] Failed to auto-save report:",
          item.key,
          err,
        );
      }
    }
    if (Object.keys(newMap).length > 0) {
      setReportIdMap((prev) => ({ ...prev, ...newMap }));
    }
  };

  // Core data loading function - Get latest results from last 30 days, limited to 20
  const loadAllAnalyses = async () => {
    if (!userUuid) {
      setError(t("email.error.notAuthenticated"));
      return;
    }

    try {
      setError(null);

      console.log(
        "EmailAnalysisSimple: Loading all analyses for user:",
        userUuid,
      );
      const results = await EmailAnalysisAPI.searchAnalyses({ userUuid });
      console.log("EmailAnalysisSimple: Received results from API:", results);
      console.log("EmailAnalysisSimple: Results length:", results?.length);

      // Transform the raw data to match the format expected by components
      const transformedResults = transformAnalysisData(results || []);
      console.log(
        "EmailAnalysisSimple: Transformed results:",
        transformedResults,
      );

      setAnalyses(transformedResults);

      // Auto-save scam reports for forwarded email analyses that don't have one yet
      ensureReportsForScamItems(transformedResults);
    } catch (err) {
      console.error("Error loading analyses:", err);

      // Check if it's a quota exceeded error (429)
      if (err.response?.status === 429) {
        const errorMessage = err.response?.data?.error || "";
        if (errorMessage.includes("Daily email analysis limit exceeded")) {
          const quotaKey = `email_quota_exceeded_${userUuid}_${new Date().toDateString()}`;
          localStorage.setItem(quotaKey, "true");
          setQuotaExceeded(true);
          return; // Don't show generic error for quota
        }
      }

      setError(t("email.error.loadFailed"));
    } finally {
      setHasLoaded(true);
    }
  };
  loadAllAnalysesRef.current = loadAllAnalyses;

  // Helper function to decode quoted-printable content as a final fallback
  const decodeQuotedPrintableSubject = (subject) => {
    console.log(
      "🔍 SUBJECT-DEBUG: ===== Frontend decodeQuotedPrintableSubject ENTRY: '" +
        subject +
        "' =====",
    );

    if (!subject || typeof subject !== "string") {
      console.log(
        "🔍 SUBJECT-DEBUG: Frontend - subject is null/empty/non-string, returning unchanged",
      );
      return subject;
    }

    try {
      // First handle MIME encoded words (=?charset?encoding?text?=)
      console.log("🔍 SUBJECT-DEBUG: Frontend - trying MIME word decoding...");
      let decoded = subject.replace(
        /=\?([^?]+)\?([QB])\?([^?]+)\?=/gi,
        (match, charset, encoding, text) => {
          console.log("🔍 SUBJECT-DEBUG: Frontend - found MIME word:", {
            match: match,
            charset: charset,
            encoding: encoding,
            text: text,
          });
          try {
            if (encoding.toUpperCase() === "Q") {
              // Quoted-printable in MIME words: replace _ with space, then decode hex
              const afterUnderscore = text.replace(/_/g, " ");
              console.log(
                "🔍 SUBJECT-DEBUG: Frontend - Q-encoding after underscore replacement:",
                afterUnderscore,
              );
              const qDecoded = decodeQuotedPrintableText(afterUnderscore);
              console.log(
                "🔍 SUBJECT-DEBUG: Frontend - Q-encoding decoded:",
                qDecoded,
              );
              return qDecoded;
            } else if (encoding.toUpperCase() === "B") {
              // Base64 in MIME words
              const bDecoded = atob(text);
              console.log(
                "🔍 SUBJECT-DEBUG: Frontend - B-encoding decoded:",
                bDecoded,
              );
              return bDecoded;
            }
            console.log(
              "🔍 SUBJECT-DEBUG: Frontend - unknown encoding, keeping original:",
              match,
            );
            return match;
          } catch (e) {
            console.warn(
              "🔍 SUBJECT-DEBUG: Frontend - failed to decode MIME word:",
              match,
              e,
            );
            return match;
          }
        },
      );

      if (decoded !== subject) {
        console.log(
          "🔍 SUBJECT-DEBUG: Frontend - MIME word decoding applied: '" +
            subject +
            "' -> '" +
            decoded +
            "'",
        );
      } else {
        console.log(
          "🔍 SUBJECT-DEBUG: Frontend - MIME word decoding did not change the text",
        );
      }

      // Then handle any remaining quoted-printable =XX patterns
      console.log(
        "🔍 SUBJECT-DEBUG: Frontend - trying quoted-printable text decoding...",
      );
      const finalDecoded = decodeQuotedPrintableText(decoded);

      if (finalDecoded !== decoded) {
        console.log(
          "🔍 SUBJECT-DEBUG: Frontend - quoted-printable decoding applied: '" +
            decoded +
            "' -> '" +
            finalDecoded +
            "'",
        );
      } else {
        console.log(
          "🔍 SUBJECT-DEBUG: Frontend - quoted-printable decoding did not change the text",
        );
      }

      console.log(
        "🔍 SUBJECT-DEBUG: ===== Frontend decodeQuotedPrintableSubject FINAL: '" +
          finalDecoded +
          "' =====",
      );

      if (finalDecoded !== subject) {
        console.log(
          "🔍 SUBJECT-DEBUG: Frontend applied complete subject decoding:",
          subject,
          "->",
          finalDecoded,
        );
      }

      return finalDecoded;
    } catch (e) {
      console.warn(
        "🔍 SUBJECT-DEBUG: Frontend subject decoding failed:",
        subject,
        e,
      );
      return subject;
    }
  };

  // Helper function to decode =XX hex patterns in quoted-printable text
  const decodeQuotedPrintableText = (text) => {
    console.log(
      "🔍 SUBJECT-DEBUG: Frontend decodeQuotedPrintableText ENTRY: '" +
        text +
        "'",
    );

    if (!text || !text.includes("=")) {
      console.log(
        "🔍 SUBJECT-DEBUG: Frontend - no = patterns found, returning unchanged",
      );
      return text;
    }

    let hexMatches = 0;
    const result = text.replace(/=([0-9A-Fa-f]{2})/g, (match, hex) => {
      hexMatches++;
      console.log(
        "🔍 SUBJECT-DEBUG: Frontend - found hex pattern #" +
          hexMatches +
          ": '" +
          match +
          "' (hex: " +
          hex +
          ")",
      );
      try {
        const charCode = parseInt(hex, 16);
        const decodedChar = String.fromCharCode(charCode);
        console.log(
          "🔍 SUBJECT-DEBUG: Frontend - hex '" +
            hex +
            "' parsed to decimal: " +
            charCode +
            ", char: '" +
            decodedChar +
            "'",
        );
        // For proper UTF-8 handling, we need to handle multi-byte sequences
        // This is a simplified approach that works for most common cases
        return decodedChar;
      } catch (e) {
        console.warn(
          "🔍 SUBJECT-DEBUG: Frontend - failed to parse hex pattern '" +
            match +
            "':",
          e,
        );
        return match;
      }
    });

    console.log(
      "🔍 SUBJECT-DEBUG: Frontend - total hex patterns found: " + hexMatches,
    );
    console.log(
      "🔍 SUBJECT-DEBUG: Frontend decodeQuotedPrintableText FINAL: '" +
        text +
        "' -> '" +
        result +
        "'",
    );
    return result;
  };

  // Helper function to transform raw analysis data
  const transformAnalysisData = (analysisData) => {
    return analysisData.map((item) => {
      // Extract the original subject before decoding
      const originalSubject =
        item.analysisResult?.analyzedEmailContent?.subject ||
        item.analysisResult?.subject ||
        "No subject";

      console.log(
        "🔍 SUBJECT-DEBUG: Frontend transformAnalysisData - original subject from API: '" +
          originalSubject +
          "'",
      );

      // Apply frontend decoding
      const decodedSubject = decodeQuotedPrintableSubject(originalSubject);
      console.log(
        "🔍 SUBJECT-DEBUG: Frontend transformAnalysisData - after frontend decoding: '" +
          decodedSubject +
          "'",
      );

      return {
        key: item.analysisKey, // Use item.analysisKey (the correct property name)
        lastModified: parseZuluTime(item.analysisResult?.analyzedAt),
        size: 0,
        originalKey: item.originalKey,
        reportId: item.analysisResult?.reportId || null, // user_scam_reports.id for sharing
        originalEmailContent: item.analysisResult?.analyzedEmailContent
          ? item.analysisResult.analyzedEmailContent.body || "No body content"
          : item.originalEmailContent,
        analysis: {
          analysisResult: {
            isScam: item.analysisResult?.isScam || false,
            confidence: item.analysisResult?.confidence || 0,
            scamType: getThreatTypeDisplay(
              item.analysisResult?.scamType,
              item.analysisResult?.confidence || 0,
            ),
            reasoning: item.analysisResult?.explanation || "",
            sender:
              item.analysisResult?.analyzedEmailContent?.senderEmail ||
              item.analysisResult?.sender ||
              "Unknown",
            subject: decodedSubject,
            emailBody:
              item.analysisResult?.analyzedEmailContent?.body ||
              item.analysisResult?.emailBody ||
              "",
            analysisDate: parseZuluTime(item.analysisResult?.analyzedAt),
            suspiciousElements: parseRiskFactors(
              item.analysisResult?.riskFactors,
            ),
            suggestions: item.analysisResult?.suggestions || "",
            analyzedEmailContent: item.analysisResult?.analyzedEmailContent,
            htmlEmailContent: item.analysisResult?.htmlEmailContent,
          },
          analysisTimestamp: parseZuluTime(item.analysisResult?.analyzedAt),
          analysisMethod: "BACKEND_API",
        },
      };
    });
  };

  // Helper function to determine threat type display based on confidence
  const getThreatTypeDisplay = (scamType, confidence) => {
    if (confidence <= 0.2) {
      return "None";
    }
    return scamType || "UNKNOWN";
  };

  // Helper function to parse risk factors
  const parseRiskFactors = (riskFactors) => {
    // If already an array, return it directly
    if (Array.isArray(riskFactors)) {
      return riskFactors;
    }

    // If it's a string, try to parse it
    if (typeof riskFactors === "string") {
      try {
        const parsed = JSON.parse(riskFactors);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        // If parsing fails, try to split by common delimiters
        return riskFactors
          .split(/[,;|\n]/)
          .map((item) => item.trim())
          .filter((item) => item.length > 0);
      }
    }

    // If it's neither array nor string, return empty array
    return [];
  };

  // Helper function to parse Zulu time
  const parseZuluTime = (timeString) => {
    if (!timeString) return null;

    try {
      // Handle various timestamp formats
      let parsedTime;

      if (typeof timeString === "string") {
        // Add 'Z' if it's missing for UTC parsing
        if (
          timeString.includes("T") &&
          !timeString.includes("Z") &&
          !timeString.includes("+")
        ) {
          timeString = timeString + "Z";
        }
        parsedTime = new Date(timeString);
      } else if (timeString instanceof Date) {
        parsedTime = timeString;
      } else {
        parsedTime = new Date(timeString);
      }

      // Check if the parsed time is valid
      if (isNaN(parsedTime.getTime())) {
        console.warn("Invalid time string:", timeString);
        return null;
      }

      return parsedTime;
    } catch (error) {
      console.warn("Error parsing time string:", timeString, error);
      return null;
    }
  };

  const handleAnalysisSelected = (analysis) => {
    setSelectedAnalysis(analysis);

    // Check if we're on mobile and switch to details tab
    if (isMobile()) {
      console.log(
        "EmailAnalysisSimple: Mobile detected, switching to details tab",
      );
      setActiveTab("details"); // Switch to details tab instead of full-screen view
    } else {
      // For desktop mode, always scroll to details section with multiple attempts
      const scrollAttempts = [50, 150, 300];
      scrollAttempts.forEach((delay, index) => {
        setTimeout(() => {
          scrollToAnalysisDetails();
          console.log(
            `EmailAnalysisSimple: Manual selection scroll attempt ${
              index + 1
            } - Scrolled to details`,
          );
        }, delay);
      });
    }
  };

  // Navigation functions
  const scrollToAnalysisResults = () => {
    const element = document.querySelector(".results-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToAnalysisDetails = () => {
    const element = document.querySelector(".details-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      console.log(
        "EmailAnalysisSimple: Successfully scrolled to details section",
      );
    } else {
      // Primary selector not found, try fallback selectors (normal for this component structure)
      const fallbackSelectors = [
        ".analysis-details",
        "[class*='details']",
        ".analysis-details-simple",
        "[class*='analysis-details']",
      ];

      for (const selector of fallbackSelectors) {
        const fallbackElement = document.querySelector(selector);
        if (fallbackElement) {
          fallbackElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          console.log(
            `EmailAnalysisSimple: Fallback scroll successful using selector: ${selector}`,
          );
          break;
        }
      }
    }
  };

  // Refresh function
  const refreshAnalyses = () => {
    loadAllAnalyses();
  };

  const onNavigateToManual = () => {
    if (setHeroContent) setHeroContent("Manual Entry");
  };

  const removeAnalysis = (key) => {
    setAnalyses((prev) => prev.filter((item) => item.key !== key));
  };

  const handleReanalyzeWithContext = async (analysis, userContext) => {
    if (!analysis?.key) {
      throw new Error("No analysis selected for re-analysis.");
    }

    const updated = await EmailAnalysisAPI.reanalyzeWithContext(
      analysis.key,
      userContext,
    );
    const transformed = transformAnalysisData([updated])[0];

    setAnalyses((prev) =>
      prev.map((item) => (item.key === transformed.key ? transformed : item)),
    );
    setSelectedAnalysis(transformed);

    // Update reportIdMap - set if exists, remove if null
    setReportIdMap((prev) => {
      const newMap = { ...prev };
      if (transformed?.reportId) {
        newMap[transformed.key] = transformed.reportId;
      } else {
        // Remove from map if reportId is null (scam cleared)
        delete newMap[transformed.key];
      }
      return newMap;
    });

    return transformed;
  };

  if (error) {
    return (
      <div className="common-container">
        <div className="common-section">
          <div className="common-section-content">
            <div className="error-message">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="icon-right-margin"
              />
              {error}
              <div className="error-actions">
                <button
                  onClick={refreshAnalyses}
                  className="common-button action-button"
                >
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="icon-right-margin"
                  />
                  {t("email.error.tryAgain")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`simple-view common-container${showMobileTabView ? " easm-mobile-active" : ""}`}
    >
      {analysisDisabled && (
        <div
          className="analysis-disabled-banner"
          style={{
            backgroundColor: "#fde8e8",
            border: "1px solid #f1aeb5",
            borderRadius: "4px",
            padding: "12px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <FontAwesomeIcon
            icon={faBan}
            style={{ color: "#b02a37", fontSize: "20px" }}
          />
          <div style={{ flex: 1 }}>
            <strong style={{ color: "#b02a37" }}>
              {t("email.analysisDisabled.title")}
            </strong>
            <p
              style={{
                margin: "4px 0 0 0",
                color: "#842029",
                fontSize: "14px",
              }}
            >
              {t("email.analysisDisabled.body")}
            </p>
          </div>
        </div>
      )}
      {quotaExceeded && (
        <div
          className="quota-exceeded-banner"
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            padding: "12px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            style={{ color: "#856404", fontSize: "20px" }}
          />
          <div style={{ flex: 1 }}>
            <strong style={{ color: "#856404" }}>
              {t("email.quota.title")}
            </strong>
            <p
              style={{
                margin: "4px 0 0 0",
                color: "#856404",
                fontSize: "14px",
              }}
            >
              {t("email.quota.body")}
            </p>
          </div>
        </div>
      )}
      {showMobileTabView ? (
        <EmailAnalysisSimpleMobile
          selectedAnalysis={selectedAnalysis}
          analyses={analyses}
          handleAnalysisSelected={handleAnalysisSelected}
          handleReanalyzeWithContext={handleReanalyzeWithContext}
          refreshAnalyses={refreshAnalyses}
          userUuid={userUuid}
          setError={setError}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onAnalysisDeleted={removeAnalysis}
          reportIdMap={reportIdMap}
          onNavigateToManual={onNavigateToManual}
          userEmail={loggedInUser?.email}
          awaitState={awaitState}
          onStartAwaiting={startAwaitingForward}
          onCancelAwaiting={cancelAwaitingForward}
          forwardingDisabled={analysisDisabled}
          hasLoaded={hasLoaded}
        />
      ) : (
        <EmailAnalysisSimpleDesktop
          selectedAnalysis={selectedAnalysis}
          analyses={analyses}
          handleAnalysisSelected={handleAnalysisSelected}
          handleReanalyzeWithContext={handleReanalyzeWithContext}
          refreshAnalyses={refreshAnalyses}
          userUuid={userUuid}
          setError={setError}
          scrollToAnalysisResults={scrollToAnalysisResults}
          onAnalysisDeleted={removeAnalysis}
          reportIdMap={reportIdMap}
          onNavigateToManual={onNavigateToManual}
          userEmail={loggedInUser?.email}
          awaitState={awaitState}
          onStartAwaiting={startAwaitingForward}
          onCancelAwaiting={cancelAwaitingForward}
          forwardingDisabled={analysisDisabled}
          hasLoaded={hasLoaded}
        />
      )}
    </div>
  );
};

export default EmailAnalysisSimple;
